import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import api from '../services/api';
import './MoodBoardEditor.css';

const CHAT_API_BASE = '/api/chat';
// Board coordinate space: everything is stored in PERCENT of the board, so it
// scales to any rendered size. The board is a fixed 3:2 rectangle.
const BOARD_RATIO = 2 / 3;          // height / width
const W_TO_H = 1 / BOARD_RATIO;     // 1.5 — multiply a width% to get the matching height%

const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } });
};

const uid = () => Math.random().toString(36).slice(2, 10);
const normUrl = (u) => (!u ? '' : (u.startsWith('/') || u.startsWith('http') ? u : '/' + u));
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Natural aspect (h/w) of an image URL, resolved by loading it.
const imageAspect = (url) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img.naturalWidth ? img.naturalHeight / img.naturalWidth : 1);
  img.onerror = () => resolve(1);
  img.src = url;
});

// Crop a tile image to a clean, seamless square. Many texture/cutout images have
// a transparent (or soft/irregular) border; tiling the raw image then lets the
// board show through between repeats — the "gaps". This trims to the opaque
// bounding box, then takes the largest centred square inside it, so the unit
// tiles edge-to-edge with nothing see-through. Returns a PNG Blob (uploaded to
// /upload/ so it persists like any tile image).
const cropToSquare = (url) => new Promise((resolve, reject) => {
  const im = new Image();
  im.crossOrigin = 'anonymous';
  im.onload = () => {
    try {
      const W = im.naturalWidth, H = im.naturalHeight;
      if (!W || !H) return reject(new Error('bad image'));
      const probe = document.createElement('canvas'); probe.width = W; probe.height = H;
      const pc = probe.getContext('2d'); pc.drawImage(im, 0, 0);
      const data = pc.getImageData(0, 0, W, H).data;
      // Opaque bounding box (alpha above a small threshold).
      let minX = W, minY = H, maxX = -1, maxY = -1;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (data[(y * W + x) * 4 + 3] >= 40) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < minX || maxY < minY) { minX = 0; minY = 0; maxX = W - 1; maxY = H - 1; } // fully transparent → whole image
      const bw = maxX - minX + 1, bh = maxY - minY + 1;
      const side = Math.min(bw, bh);
      const sx = minX + Math.floor((bw - side) / 2);
      const sy = minY + Math.floor((bh - side) / 2);
      const out = document.createElement('canvas'); out.width = side; out.height = side;
      out.getContext('2d').drawImage(im, sx, sy, side, side, 0, 0, side, side);
      out.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
    } catch (e) { reject(e); }
  };
  im.onerror = () => reject(new Error('load failed'));
  im.src = url;
});

const MoodBoardEditor = ({ projectId }) => {
  // ---- view + board list -------------------------------------------------
  const [view, setView] = useState('list');        // 'list' | 'editor'
  const [boards, setBoards] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // ---- open board --------------------------------------------------------
  const [boardId, setBoardId] = useState(null);
  const [name, setName] = useState('');
  const [board, setBoard] = useState({ background: { color: '#f4f1ea' }, items: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);     // text item being edited
  const [saveState, setSaveState] = useState('saved');  // 'saved' | 'saving' | 'dirty'

  // ---- image picker ------------------------------------------------------
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState('image');   // 'image' | 'newSwatch' | 'setSwatch'
  const [pickerTarget, setPickerTarget] = useState(null);  // swatch id for 'setSwatch'
  const [pickerTab, setPickerTab] = useState('products');
  const [pickerImages, setPickerImages] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [busy, setBusy] = useState(false);   // uploading a paste / removing a background

  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 1, h: 1 });
  const dragRef = useRef(null);       // active pointer interaction
  const saveTimer = useRef(null);
  const uploadInput = useRef(null);

  // ---- full-screen presentation ----
  const presRef = useRef(null);       // the fullscreen overlay element
  const presStageRef = useRef(null);  // the area the board is fitted into
  const [presenting, setPresenting] = useState(false);
  const [presSize, setPresSize] = useState({ w: 1, h: 1 });

  // ---- manual erase / restore brush ----
  const [brushItem, setBrushItem] = useState(null);   // image item being touched up
  const [brushSize, setBrushSize] = useState(36);     // display px
  const [brushMode, setBrushMode] = useState('erase'); // 'erase' | 'restore'
  const [brushBusy, setBrushBusy] = useState(false);
  const brushCanvasRef = useRef(null);
  const brushOrigRef = useRef(null);   // original image element, for Restore
  const brushStrokeRef = useRef(null); // { drawing, lastX, lastY } during a stroke

  // -------------------- board list --------------------
  const loadBoards = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/mood-board.php?project_id=${encodeURIComponent(projectId)}`);
      const d = await res.json().catch(() => ({}));
      if (d.success) setBoards(d.data || []);
    } catch (e) { setMsg('Could not load boards.'); }
    finally { setListLoading(false); }
  }, [projectId]);

  useEffect(() => { if (view === 'list') loadBoards(); }, [view, loadBoards]);

  const createBoard = async () => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/mood-board.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, name: 'Untitled board' }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) openBoardData(d.data);
      else setMsg(d.message || 'Could not create board.');
    } catch (e) { setMsg('Could not create board.'); }
  };

  const openBoard = async (id) => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/mood-board.php?id=${encodeURIComponent(id)}`);
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) openBoardData(d.data);
      else setMsg(d.message || 'Could not open board.');
    } catch (e) { setMsg('Could not open board.'); }
  };

  const openBoardData = (data) => {
    setBoardId(data.id);
    setName(data.name || 'Untitled board');
    setBoard(data.board && Array.isArray(data.board.items) ? data.board : { background: { color: '#f4f1ea' }, items: [] });
    setSelectedId(null); setEditingId(null); setSaveState('saved');
    setView('editor');
  };

  const deleteBoard = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this mood board? This cannot be undone.')) return;
    try {
      await authFetch(`${CHAT_API_BASE}/mood-board.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      setBoards(prev => prev.filter(b => b.id !== id));
    } catch (e2) { setMsg('Could not delete board.'); }
  };

  // -------------------- autosave --------------------
  const queueSave = useCallback((nextBoard) => {
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await authFetch(`${CHAT_API_BASE}/mood-board.php`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: boardId, board: nextBoard }),
        });
        setSaveState('saved');
      } catch (e) { setSaveState('dirty'); }
    }, 600);
  }, [boardId]);

  // Apply a board mutation + schedule a save.
  const mutate = useCallback((fn) => {
    setBoard(prev => {
      const next = fn(prev);
      queueSave(next);
      return next;
    });
  }, [queueSave]);

  const saveName = async (value) => {
    const v = value.trim() || 'Untitled board';
    setName(v);
    try {
      await authFetch(`${CHAT_API_BASE}/mood-board.php`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: boardId, name: v }),
      });
    } catch (e) { /* non-fatal */ }
  };

  // -------------------- canvas measuring --------------------
  useEffect(() => {
    if (view !== 'editor' || !canvasRef.current) return;
    const el = canvasRef.current;
    const update = () => setCanvasSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [view]);

  // -------------------- full-screen presentation --------------------
  // Fit the 3:2 board inside the available screen area (letterboxed), and keep
  // that size in state so tiled swatches and text scale to the big view.
  useEffect(() => {
    if (!presenting) return;
    const fit = () => {
      const stage = presStageRef.current;
      if (!stage) return;
      const availW = stage.clientWidth * 0.96, availH = stage.clientHeight * 0.96;
      let w = availW, h = w * BOARD_RATIO;
      if (h > availH) { h = availH; w = h / BOARD_RATIO; }
      setPresSize({ w: Math.max(1, Math.round(w)), h: Math.max(1, Math.round(h)) });
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (presStageRef.current) ro.observe(presStageRef.current);
    window.addEventListener('resize', fit);
    // Leaving browser fullscreen (Esc / F11) also closes the presentation.
    const onFsChange = () => { if (!document.fullscreenElement) setPresenting(false); };
    document.addEventListener('fullscreenchange', onFsChange);
    const onKey = (e) => { if (e.key === 'Escape') setPresenting(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
      document.removeEventListener('fullscreenchange', onFsChange);
      window.removeEventListener('keydown', onKey);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, [presenting]);

  // Enter the presentation. flushSync renders the overlay synchronously so the
  // fullscreen request still happens inside the click's user gesture (browsers
  // require that); if fullscreen is blocked the overlay still fills the window.
  const startPresent = () => {
    flushSync(() => setPresenting(true));
    const el = presRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  // -------------------- item helpers --------------------
  const items = board.items;
  const selected = items.find(it => it.id === selectedId) || null;
  const maxZ = items.reduce((m, it) => Math.max(m, it.z || 0), 0);
  const minZ = items.reduce((m, it) => Math.min(m, it.z || 0), 0);

  const addItem = (it) => {
    const id = uid();
    // Compute z from the freshest items so paste/async adds always land on top.
    mutate(prev => {
      const z = prev.items.reduce((m, x) => Math.max(m, x.z || 0), 0) + 1;
      return { ...prev, items: [...prev.items, { id, x: 36, y: 34, rotation: 0, z, ...it }] };
    });
    setSelectedId(id);
  };

  const updateItem = (id, patch) => mutate(prev => ({
    ...prev, items: prev.items.map(it => it.id === id ? { ...it, ...patch } : it),
  }));

  const removeItem = (id) => { mutate(prev => ({ ...prev, items: prev.items.filter(it => it.id !== id) })); setSelectedId(null); setEditingId(null); };

  const bringForward = (id) => updateItem(id, { z: maxZ + 1 });
  // Floor at 0 so the computed CSS z-index (z+1) stays ≥ 1 and the item can't
  // drop behind the canvas background and become unreachable.
  const sendBack = (id) => updateItem(id, { z: Math.max(0, minZ - 1) });

  const addImageFromUrl = async (url, aspect, sourceUrl) => {
    const u = normUrl(url);
    const asp = aspect || await imageAspect(u);
    const w = 28;
    const extra = sourceUrl ? { sourceUrl } : {};
    addItem({ type: 'image', url: u, w, h: clamp(w * asp * W_TO_H, 4, 120), radius: 0, ...extra });
  };
  const addText = () => addItem({ type: 'text', text: 'Double-click to edit', color: '#2b2720', fontPct: 4.5, align: 'center', bold: false, italic: false, w: 34, h: 12 });
  const addSwatch = () => addItem({ type: 'swatch', color: '#c2b280', label: '', radius: 10, w: 12, h: 12 * W_TO_H });

  // Upload a Blob/File (clipboard paste, or a processed canvas) and return {url,aspect}.
  const uploadBlob = async (blob, filename = 'image.png') => {
    const fd = new FormData();
    fd.append('file', blob, filename);
    fd.append('project_id', projectId);
    const res = await authFetch(`${CHAT_API_BASE}/mood-board-upload.php`, { method: 'POST', body: fd });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error(d.error || 'Upload failed');
    return d.data;
  };

  // Paste from clipboard (a screen capture or a copied web image).
  const uploadBlobAsImage = async (blob, sourceUrl) => {
    setBusy(true);
    try { const d = await uploadBlob(blob, 'pasted.png'); await addImageFromUrl(d.url, d.aspect, sourceUrl); }
    catch (e) { setMsg('Could not add the pasted image.'); }
    finally { setBusy(false); }
  };

  // Pull a *page* URL from a paste event — the item's source page, not the
  // image file. Chrome's "Copy image" puts the image CDN URL on the clipboard,
  // which we deliberately skip; a wrapping <a href> is the only reliable
  // auto-capture (common when copying an image inside a product tile). The
  // user can always set it by hand via the Source URL input on the item.
  const looksLikeImageFile = (u) => /\.(jpe?g|png|gif|webp|avif|bmp|svg|heic)(\?|#|$)/i.test(u);
  const extractSourceUrl = (cd) => {
    if (!cd) return null;
    const safe = (s) => (typeof s === 'string' && /^https?:\/\//i.test(s.trim())) ? s.trim() : null;
    const html = cd.getData && cd.getData('text/html');
    if (html) {
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const a = doc.querySelector('a[href]');
        const href = a && safe(a.getAttribute('href'));
        if (href && !looksLikeImageFile(href)) return href;
      } catch (_) { /* ignore */ }
    }
    const uri = safe(cd.getData && cd.getData('text/uri-list'));
    if (uri) {
      const first = uri.split(/\r?\n/).find(x => /^https?:/i.test(x));
      if (first && !looksLikeImageFile(first)) return first;
    }
    return null;
  };

  // Quick, offline cutout: make the connected edge background transparent via a
  // corner-seeded flood fill. Best on a plain/solid background. Returns a Blob.
  const floodFillCutout = (url) => new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => {
      try {
        const maxD = 1600;
        const scale = Math.min(1, maxD / (Math.max(im.naturalWidth, im.naturalHeight) || 1));
        const W = Math.max(1, Math.round(im.naturalWidth * scale));
        const H = Math.max(1, Math.round(im.naturalHeight * scale));
        const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
        const ctx = cv.getContext('2d'); ctx.drawImage(im, 0, 0, W, H);
        const imgData = ctx.getImageData(0, 0, W, H);
        const data = imgData.data;
        const corners = [0, W - 1, (H - 1) * W, (H - 1) * W + (W - 1)];
        let br = 0, bg = 0, bb = 0;
        corners.forEach(ci => { br += data[ci * 4]; bg += data[ci * 4 + 1]; bb += data[ci * 4 + 2]; });
        br /= 4; bg /= 4; bb /= 4;
        const tol2 = 52 * 52 * 3;
        const visited = new Uint8Array(W * H);
        const stack = [];
        for (let x = 0; x < W; x++) { stack.push(x); stack.push((H - 1) * W + x); }
        for (let y = 0; y < H; y++) { stack.push(y * W); stack.push(y * W + (W - 1)); }
        while (stack.length) {
          const idx = stack.pop();
          if (idx < 0 || idx >= W * H || visited[idx]) continue;
          visited[idx] = 1;
          const o = idx * 4, dr = data[o] - br, dg = data[o + 1] - bg, db = data[o + 2] - bb;
          if (dr * dr + dg * dg + db * db <= tol2) {
            data[o + 3] = 0;
            const x = idx % W, y = (idx - x) / W;
            if (x > 0) stack.push(idx - 1);
            if (x < W - 1) stack.push(idx + 1);
            if (y > 0) stack.push(idx - W);
            if (y < H - 1) stack.push(idx + W);
          }
        }
        ctx.putImageData(imgData, 0, 0);
        cv.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
      } catch (e) { reject(e); }
    };
    im.onerror = reject;
    im.src = url;
  });

  // Remove an image's background. Tries studio-quality AI matting first
  // (@imgly/background-removal, lazy-loaded — downloads its model on first use
  // and handles complex edges), then falls back to the quick flood fill if the
  // AI library can't run (e.g. offline). Keeps the original as origUrl.
  const removeBackground = async (it) => {
    setBusy(true);
    setMsg('Removing background… the first run loads the AI model, so give it a moment.');
    let blob = null, usedAI = false;
    try {
      const mod = await import('@imgly/background-removal');
      const run = mod.removeBackground || (mod.default && mod.default.removeBackground) || mod.default;
      if (typeof run === 'function') {
        try {
          // Self-hosted model (served from this tenant at /imgly/). The AI matte is
          // semantic, so it separates the object even when the item colour is close
          // to the background colour — which the colour-based flood fill cannot.
          blob = await run(it.url, { publicPath: `${window.location.origin}/imgly/`, model: 'small' });
        } catch (e1) {
          // Self-hosted assets unavailable → fall back to the library's default CDN.
          blob = await run(it.url, { model: 'small' });
        }
        usedAI = !!blob;
      }
    } catch (e) { blob = null; }
    try {
      if (!blob) blob = await floodFillCutout(it.url);   // offline last resort
      const d = await uploadBlob(blob, 'cutout.png');
      updateItem(it.id, { url: normUrl(d.url), origUrl: it.origUrl || it.url });
      setMsg(usedAI ? null
        : 'Used the basic cutout (AI model unavailable) — it struggles when the background is close to the item colour. Use ✂ Erase to touch it up.');
    } catch (e) {
      setMsg('Background removal failed. Try an image with a plainer background, or use ✂ Erase.');
    } finally { setBusy(false); }
  };

  const restoreBackground = (it) => updateItem(it.id, { url: it.origUrl, origUrl: undefined });

  // -------------------- manual erase / restore brush --------------------
  const openBrush = (it) => { setBrushMode('erase'); setBrushItem(it); };
  const closeBrush = () => { setBrushItem(null); brushOrigRef.current = null; brushStrokeRef.current = null; };

  // Load the item image onto the brush canvas (and the original, for Restore).
  useEffect(() => {
    if (!brushItem) return;
    let cancelled = false;
    const base = new Image(); base.crossOrigin = 'anonymous';
    base.onload = () => {
      if (cancelled) return;
      const cv = brushCanvasRef.current; if (!cv) return;
      const maxD = 1400;
      const s = Math.min(1, maxD / (Math.max(base.naturalWidth, base.naturalHeight) || 1));
      cv.width = Math.max(1, Math.round(base.naturalWidth * s));
      cv.height = Math.max(1, Math.round(base.naturalHeight * s));
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.drawImage(base, 0, 0, cv.width, cv.height);
    };
    base.src = brushItem.url;
    const orig = new Image(); orig.crossOrigin = 'anonymous';
    orig.onload = () => { if (!cancelled) brushOrigRef.current = orig; };
    orig.src = brushItem.origUrl || brushItem.url;
    return () => { cancelled = true; };
  }, [brushItem]);

  const brushPoint = (e) => {
    const cv = brushCanvasRef.current; const rect = cv.getBoundingClientRect();
    const sx = cv.width / rect.width;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * (cv.height / rect.height), r: Math.max(1, brushSize * sx / 2) };
  };
  const brushDab = (ctx, x, y, r) => {
    if (brushMode === 'erase') {
      ctx.save(); ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    } else {
      const orig = brushOrigRef.current; if (!orig) return;
      ctx.save(); ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.clip();
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(orig, 0, 0, ctx.canvas.width, ctx.canvas.height); ctx.restore();
    }
  };
  const brushStart = (e) => {
    e.preventDefault();
    const cv = brushCanvasRef.current; if (!cv) return;
    try { cv.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    const p = brushPoint(e);
    brushDab(cv.getContext('2d'), p.x, p.y, p.r);
    brushStrokeRef.current = { drawing: true, lastX: p.x, lastY: p.y };
  };
  const brushMove = (e) => {
    const st = brushStrokeRef.current; if (!st || !st.drawing) return;
    const cv = brushCanvasRef.current; const ctx = cv.getContext('2d');
    const p = brushPoint(e);
    const dx = p.x - st.lastX, dy = p.y - st.lastY;
    const n = Math.max(1, Math.ceil(Math.hypot(dx, dy) / Math.max(1, p.r * 0.4)));
    for (let i = 1; i <= n; i++) brushDab(ctx, st.lastX + dx * i / n, st.lastY + dy * i / n, p.r);
    st.lastX = p.x; st.lastY = p.y;
  };
  const brushEnd = () => { if (brushStrokeRef.current) brushStrokeRef.current.drawing = false; };
  const applyBrush = async () => {
    const cv = brushCanvasRef.current, it = brushItem; if (!cv || !it) return;
    setBrushBusy(true);
    try {
      const blob = await new Promise((res, rej) => cv.toBlob(b => b ? res(b) : rej(new Error('toBlob')), 'image/png'));
      const d = await uploadBlob(blob, 'erased.png');
      updateItem(it.id, { url: normUrl(d.url), origUrl: it.origUrl || it.url });
      closeBrush();
    } catch (e) { setMsg('Could not save the edited image.'); }
    finally { setBrushBusy(false); }
  };

  // Picker can target: a new image, a new material swatch, or an existing swatch.
  const handlePicked = async (url, aspect) => {
    const u = normUrl(url);
    if (pickerMode === 'newSwatch') addItem({ type: 'swatch', url: u, color: '#c2b280', label: '', radius: 0, w: 12, h: 12 * W_TO_H });
    else if (pickerMode === 'setSwatch' && pickerTarget) updateItem(pickerTarget, { url: u });
    else await addImageFromUrl(u, aspect);
    setPickerOpen(false);
  };

  // -------------------- pointer interactions --------------------
  const startInteraction = (e, id, mode) => {
    e.preventDefault(); e.stopPropagation();
    const it = items.find(x => x.id === id); if (!it) return;
    setSelectedId(id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      mode, id, rect,
      startX: e.clientX, startY: e.clientY,
      orig: { x: it.x, y: it.y, w: it.w, h: it.h, rotation: it.rotation || 0 },
      center: {
        x: rect.left + (it.x + it.w / 2) / 100 * rect.width,
        y: rect.top + (it.y + it.h / 2) / 100 * rect.height,
      },
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const { rect, orig } = d;
    if (d.mode === 'drag') {
      const dx = (e.clientX - d.startX) / rect.width * 100;
      const dy = (e.clientY - d.startY) / rect.height * 100;
      updateItem(d.id, { x: clamp(orig.x + dx, -40, 140), y: clamp(orig.y + dy, -40, 140) });
    } else if (d.mode === 'resize') {
      // Resize in the item's local frame so rotation feels natural, and keep the
      // aspect ratio: project the corner drag onto the item's diagonal and scale
      // width + height by the same factor.
      const dxPx = e.clientX - d.startX, dyPx = e.clientY - d.startY;
      const th = (orig.rotation || 0) * Math.PI / 180;
      const localX = dxPx * Math.cos(th) + dyPx * Math.sin(th);
      const localY = -dxPx * Math.sin(th) + dyPx * Math.cos(th);
      const owPx = orig.w / 100 * rect.width;   // item's current pixel size
      const ohPx = orig.h / 100 * rect.height;
      const diag = Math.hypot(owPx, ohPx) || 1;
      const proj = (localX * owPx + localY * ohPx) / diag;   // drag along the diagonal
      // Clamp the scale so both dimensions stay within [3, 200]% without breaking the ratio.
      const scale = clamp(1 + proj / diag, Math.max(3 / orig.w, 3 / orig.h), Math.min(200 / orig.w, 200 / orig.h));
      updateItem(d.id, { w: orig.w * scale, h: orig.h * scale });
    } else if (d.mode === 'rotate') {
      const ang = Math.atan2(e.clientY - d.center.y, e.clientX - d.center.x) * 180 / Math.PI + 90;
      updateItem(d.id, { rotation: Math.round(ang) });
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  };

  // Keyboard delete of the selected item (not while typing in a field).
  useEffect(() => {
    if (view !== 'editor') return;
    const onKey = (e) => {
      if (editingId) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) { e.preventDefault(); removeItem(selectedId); }
      if (e.key === 'Escape') { setSelectedId(null); setEditingId(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, selectedId, editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paste an image from the clipboard (screen capture or copied web image).
  useEffect(() => {
    if (view !== 'editor') return;
    const onPaste = async (e) => {
      const ae = document.activeElement;
      const tag = (ae && ae.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (ae && ae.isContentEditable)) return; // keep text paste
      const list = (e.clipboardData && e.clipboardData.items) || [];
      for (let i = 0; i < list.length; i++) {
        if (list[i].type && list[i].type.indexOf('image') === 0) {
          const blob = list[i].getAsFile();
          if (blob) {
            e.preventDefault();
            const src = extractSourceUrl(e.clipboardData);
            await uploadBlobAsImage(blob, src);
            break;
          }
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [view, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------- image picker --------------------
  const openPicker = async (mode = 'image', targetId = null) => {
    setPickerMode(mode); setPickerTarget(targetId);
    setPickerOpen(true); setPickerTab('products');
    setPickerLoading(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-products.php?project_id=${encodeURIComponent(projectId)}`);
      const d = await res.json().catch(() => ({}));
      const rows = d.data || d.products || (Array.isArray(d) ? d : []);
      const imgs = [];
      const seen = new Set();
      rows.forEach(r => {
        const p = r.image_path || r.image_url || (r.attributes && r.attributes.image_path);
        if (p && !seen.has(p)) { seen.add(p); imgs.push({ url: normUrl(p), label: r.name || (r.attributes && r.attributes.name) || '' }); }
      });
      setPickerImages(imgs);
    } catch (e) { setPickerImages([]); }
    finally { setPickerLoading(false); }
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPickerLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      const res = await authFetch(`${CHAT_API_BASE}/mood-board-upload.php`, { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) { await handlePicked(d.data.url, d.data.aspect); }
      else setMsg(d.error || 'Upload failed.');
    } catch (e2) { setMsg('Upload failed.'); }
    finally { setPickerLoading(false); }
  };

  // -------------------- export PNG --------------------
  const exportPng = async () => {
    const EXPORT_W = 1800;
    const EXPORT_H = Math.round(EXPORT_W * BOARD_RATIO);
    const cv = document.createElement('canvas');
    cv.width = EXPORT_W; cv.height = EXPORT_H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = board.background?.color || '#f4f1ea';
    ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);

    // Preload any item backed by an image (image items + material swatches).
    const imgEls = {};
    await Promise.all(items.filter(it => it.url).map(it => new Promise(res => {
      const img = new Image();
      img.onload = () => { imgEls[it.id] = img; res(); };
      img.onerror = () => res();
      img.src = it.url;
    })));

    const roundRect = (c, x, y, w, h, r) => {
      r = Math.min(r, w / 2, h / 2);
      c.beginPath();
      c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r);
      c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
    };

    [...items].sort((a, b) => (a.z || 0) - (b.z || 0)).forEach(it => {
      const w = it.w / 100 * EXPORT_W, h = it.h / 100 * EXPORT_H;
      const cx = it.x / 100 * EXPORT_W + w / 2, cy = it.y / 100 * EXPORT_H + h / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((it.rotation || 0) * Math.PI / 180);
      if (it.type === 'image' && imgEls[it.id]) {
        if (it.radius) { roundRect(ctx, -w / 2, -h / 2, w, h, it.radius / 100 * Math.min(w, h)); ctx.clip(); }
        ctx.drawImage(imgEls[it.id], -w / 2, -h / 2, w, h);
      } else if (it.type === 'swatch') {
        // Label area sizes to the font (matches the DOM's flex layout: label is
        // natural-height, tile takes the rest), not 20% of the item — otherwise
        // large tiled swatches get a towering label strip.
        const hasLabel = !!it.label;
        const labelFs = hasLabel ? Math.max(12, EXPORT_H * 0.016) : 0;
        const labelAreaH = hasLabel ? labelFs * 1.5 : 0;
        const tileH = Math.max(1, h - labelAreaH);
        const r = (it.radius || 0) / 100 * Math.min(w, tileH);
        ctx.save();
        roundRect(ctx, -w / 2, -h / 2, w, tileH, r); ctx.clip();
        if (it.url && imgEls[it.id] && it.tileW && it.tileH) {
          const tWpx = Math.max(1, Math.round(it.tileW / 100 * EXPORT_W));
          const tHpx = Math.max(1, Math.round(it.tileH / 100 * EXPORT_H));
          const off = document.createElement('canvas');
          off.width = tWpx; off.height = tHpx;
          off.getContext('2d').drawImage(imgEls[it.id], 0, 0, tWpx, tHpx);
          const pat = ctx.createPattern(off, 'repeat');
          ctx.save();
          ctx.translate(-w / 2, -h / 2);
          // Backing colour fills any transparent gaps between tile repeats.
          ctx.fillStyle = it.color || '#f4f1ea';
          ctx.fillRect(0, 0, w, tileH);
          ctx.fillStyle = pat;
          ctx.fillRect(0, 0, w, tileH);
          ctx.restore();
        } else if (it.url && imgEls[it.id]) {
          if (it.color) { ctx.fillStyle = it.color; ctx.fillRect(-w / 2, -h / 2, w, tileH); }
          ctx.drawImage(imgEls[it.id], -w / 2, -h / 2, w, tileH);
        }
        else { ctx.fillStyle = it.color || '#c2b280'; ctx.fillRect(-w / 2, -h / 2, w, tileH); }
        ctx.restore();
        if (hasLabel) {
          ctx.fillStyle = '#2b2720';
          ctx.font = `600 ${labelFs}px Georgia, serif`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(it.label, 0, -h / 2 + tileH + labelAreaH / 2);
        }
      } else if (it.type === 'text') {
        const fs = (it.fontPct || 4) / 100 * EXPORT_H;
        ctx.fillStyle = it.color || '#2b2720';
        ctx.font = `${it.italic ? 'italic ' : ''}${it.bold ? '700 ' : '400 '}${fs}px Georgia, serif`;
        ctx.textBaseline = 'top';
        ctx.textAlign = it.align || 'left';
        const words = String(it.text || '').split(/\s+/);
        const lines = []; let line = '';
        words.forEach(word => {
          const test = line ? line + ' ' + word : word;
          if (ctx.measureText(test).width > w && line) { lines.push(line); line = word; } else line = test;
        });
        if (line) lines.push(line);
        const tx = it.align === 'center' ? 0 : it.align === 'right' ? w / 2 : -w / 2;
        lines.forEach((ln, i) => ctx.fillText(ln, tx, -h / 2 + i * fs * 1.25));
      }
      ctx.restore();
    });

    cv.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (name || 'mood-board').replace(/[^\w.-]+/g, '_') + '.png';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  };

  // ======================================================================
  // RENDER
  // ======================================================================
  if (view === 'list') {
    return (
      <div className="mb-wrap">
        <div className="mb-list-head">
          <div>
            <h2 className="mb-title">Mood Boards</h2>
            <p className="mb-sub">Collect images, swatches, and notes for this project.</p>
          </div>
          <button className="mb-btn mb-btn-primary" onClick={createBoard}>+ New board</button>
        </div>
        {msg && <div className="mb-msg">{msg}</div>}
        {listLoading ? (
          <div className="mb-empty">Loading…</div>
        ) : boards.length === 0 ? (
          <div className="mb-empty">
            <div className="mb-empty-icon" aria-hidden="true">🎨</div>
            <div className="mb-empty-title">No mood boards yet</div>
            <div className="mb-empty-body">Create your first board to start collecting ideas.</div>
          </div>
        ) : (
          <div className="mb-grid">
            {boards.map(b => (
              <div key={b.id} className="mb-card" onClick={() => openBoard(b.id)} role="button" tabIndex={0}
                   onKeyDown={(e) => e.key === 'Enter' && openBoard(b.id)}>
                <div className="mb-card-cover" style={b.cover ? { backgroundImage: `url(${normUrl(b.cover)})` } : undefined}>
                  {!b.cover && <span className="mb-card-cover-ph">🎨</span>}
                </div>
                <div className="mb-card-meta">
                  <div className="mb-card-name" title={b.name}>{b.name}</div>
                  <div className="mb-card-count">{b.item_count || 0} item{(b.item_count || 0) === 1 ? '' : 's'}</div>
                </div>
                <button className="mb-card-del" title="Delete board" onClick={(e) => deleteBoard(b.id, e)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- editor ----
  const fontPx = (it) => (it.fontPct || 4) / 100 * canvasSize.h;

  // Read-only visual for one item, sized to the given board dimensions. Shared
  // by the editor canvas and the full-screen presentation so both render the
  // same thing (tiled swatches and text scale to whichever board size).
  const itemVisual = (it, size) => {
    if (it.type === 'image') {
      return <img src={it.url} alt="" draggable={false}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: `${(it.radius || 0)}%`, display: 'block' }} />;
    }
    if (it.type === 'swatch') {
      return (
        <div className="mb-swatch">
          <div className="mb-swatch-tile"
               style={it.url
                 ? (it.tileW
                     ? {
                         // Backing colour fills any transparent areas so the
                         // board doesn't show through between tile repeats.
                         backgroundColor: it.color || '#f4f1ea',
                         backgroundImage: `url(${it.url})`,
                         backgroundSize: `${Math.round(it.tileW / 100 * size.w)}px ${Math.round(it.tileH / 100 * size.h)}px`,
                         backgroundRepeat: 'repeat',
                         backgroundPosition: '0 0',
                         borderRadius: `${(it.radius || 0)}%`,
                       }
                     : { backgroundColor: it.color || 'transparent', backgroundImage: `url(${it.url})`, borderRadius: `${(it.radius || 0)}%` })
                 : { background: it.color, borderRadius: `${(it.radius || 0)}%` }} />
          {it.label ? <div className="mb-swatch-label" style={{ fontSize: Math.max(9, size.h * 0.016) }}>{it.label}</div> : null}
        </div>
      );
    }
    if (it.type === 'text') {
      const fs = (it.fontPct || 4) / 100 * size.h;
      return (
        <div className="mb-text" style={{ fontSize: fs, color: it.color, textAlign: it.align, fontWeight: it.bold ? 700 : 400, fontStyle: it.italic ? 'italic' : 'normal' }}>
          {it.text || ' '}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mb-wrap mb-editor">
      <div className="mb-toolbar">
        <button className="mb-btn" onClick={() => { setView('list'); }}>← Boards</button>
        <input className="mb-name-input" value={name}
               onChange={(e) => setName(e.target.value)} onBlur={(e) => saveName(e.target.value)}
               aria-label="Board name" />
        <div className="mb-tools">
          <button className="mb-btn" onClick={() => openPicker('image')}>🖼 Image</button>
          <button className="mb-btn" onClick={addText}>T Text</button>
          <button className="mb-btn" onClick={addSwatch}>🎨 Swatch</button>
          <button className="mb-btn" onClick={() => openPicker('newSwatch')}>🧵 Material</button>
          <label className="mb-btn mb-bg">
            Background
            <input type="color" value={board.background?.color || '#f4f1ea'}
                   onChange={(e) => mutate(prev => ({ ...prev, background: { color: e.target.value } }))} />
          </label>
          <button className="mb-btn" title="Present full screen" onClick={startPresent}>⛶ Present</button>
          <button className="mb-btn mb-btn-primary" onClick={exportPng}>⬇ Export PNG</button>
          <span className={`mb-save mb-save-${saveState}`}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'dirty' ? 'Unsaved' : 'Saved'}
          </span>
        </div>
      </div>

      {/* contextual controls for the selected item */}
      {selected && (
        <div className="mb-context">
          <span className="mb-context-label">{selected.type}</span>
          {selected.type === 'text' && (
            <>
              <button className="mb-mini" title="Bold" onClick={() => updateItem(selected.id, { bold: !selected.bold })} aria-pressed={selected.bold}><b>B</b></button>
              <button className="mb-mini" title="Italic" onClick={() => updateItem(selected.id, { italic: !selected.italic })} aria-pressed={selected.italic}><i>I</i></button>
              <button className="mb-mini" title="Align" onClick={() => updateItem(selected.id, { align: selected.align === 'left' ? 'center' : selected.align === 'center' ? 'right' : 'left' })}>≋</button>
              <button className="mb-mini" title="Smaller" onClick={() => updateItem(selected.id, { fontPct: clamp((selected.fontPct || 4) - 0.5, 0.5, 40) })}>A−</button>
              <button className="mb-mini" title="Larger" onClick={() => updateItem(selected.id, { fontPct: clamp((selected.fontPct || 4) + 0.5, 0.5, 40) })}>A+</button>
              <input className="mb-color" type="color" title="Text color" value={selected.color || '#2b2720'} onChange={(e) => updateItem(selected.id, { color: e.target.value })} />
            </>
          )}
          {selected.type === 'swatch' && (
            <>
              {(!selected.url || selected.tileW) && (
                <input className="mb-color" type="color"
                       title={selected.url ? 'Backing colour — fills any gaps between tiles' : 'Swatch colour'}
                       value={selected.color || '#c2b280'} onChange={(e) => updateItem(selected.id, { color: e.target.value })} />
              )}
              <input className="mb-label-input" placeholder="Label" value={selected.label || ''} onChange={(e) => updateItem(selected.id, { label: e.target.value })} />
              <button className="mb-mini mb-mini-wide" onClick={() => openPicker('setSwatch', selected.id)}>{selected.url ? 'Replace' : 'Image'}</button>
              {selected.url && (
                <button className="mb-mini mb-mini-wide" title="Use a solid colour instead" onClick={() => updateItem(selected.id, { url: undefined })}>Color</button>
              )}
              <button className="mb-mini mb-mini-wide" disabled={busy}
                      title={selected.tileW ? 'Stop tiling — swatch fills the box' : 'Crop to a clean square tile and repeat it to fill the box'}
                      aria-pressed={!!selected.tileW}
                      onClick={async () => {
                        if (selected.tileW) { updateItem(selected.id, { tileW: undefined, tileH: undefined }); return; }
                        if (!selected.url) { updateItem(selected.id, { tileW: selected.w, tileH: selected.h }); return; }
                        // Image/material swatch: crop to a clean square tile first so
                        // the transparent border that let the board show through is gone,
                        // then tile it with a square cell (tileH = tileW × 1.5 of the
                        // 3:2 board) so repeats butt up edge-to-edge with no gaps.
                        setBusy(true);
                        try {
                          const blob = await cropToSquare(selected.url);
                          const d = await uploadBlob(blob, 'tile.png');
                          updateItem(selected.id, {
                            url: normUrl(d.url), origUrl: selected.origUrl || selected.url,
                            tileW: selected.w, tileH: selected.w * W_TO_H,
                          });
                        } catch (e) {
                          updateItem(selected.id, { tileW: selected.w, tileH: selected.w * W_TO_H });
                          setMsg('Could not crop the tile; tiling the original image.');
                        } finally { setBusy(false); }
                      }}>
                ⊞ Tile
              </button>
              {selected.tileW && (
                <>
                  <button className="mb-mini" title="Smaller tiles" onClick={() => updateItem(selected.id, { tileW: Math.max(0.5, (selected.tileW || 0) * 0.8), tileH: Math.max(0.5, (selected.tileH || 0) * 0.8) })}>▦−</button>
                  <button className="mb-mini" title="Larger tiles" onClick={() => updateItem(selected.id, { tileW: Math.min(100, (selected.tileW || 0) * 1.25), tileH: Math.min(100, (selected.tileH || 0) * 1.25) })}>▦+</button>
                </>
              )}
            </>
          )}
          {selected.type === 'image' && (
            <>
              <button className="mb-mini mb-mini-wide" disabled={busy}
                      title={selected.origUrl ? 'Restore the original background' : 'Make the background transparent (best on a plain background)'}
                      onClick={() => selected.origUrl ? restoreBackground(selected) : removeBackground(selected)}>
                {busy ? '…' : (selected.origUrl ? 'Restore BG' : 'Remove BG')}
              </button>
              <button className="mb-mini mb-mini-wide" disabled={busy}
                      title="Manually erase or paint back parts of the image"
                      onClick={() => openBrush(selected)}>✂ Erase</button>
              <button className="mb-mini mb-mini-wide"
                      title="Convert this image into a material swatch (can be labeled and tiled)"
                      onClick={() => updateItem(selected.id, { type: 'swatch', color: selected.color || '#c2b280', label: selected.label || '' })}>
                🎨 To Swatch
              </button>
              <input className="mb-label-input" style={{ minWidth: 220 }}
                     placeholder="Source URL (page the item came from)"
                     title="The ↗ badge opens this URL. Leave blank to hide the badge."
                     value={selected.sourceUrl || ''}
                     onChange={(e) => {
                       const v = e.target.value.trim();
                       updateItem(selected.id, { sourceUrl: v || undefined });
                     }} />
            </>
          )}
          <button className="mb-mini" title="Bring forward" onClick={() => bringForward(selected.id)}>▲</button>
          <button className="mb-mini" title="Send back" onClick={() => sendBack(selected.id)}>▼</button>
          <button className="mb-mini mb-mini-danger" title="Delete" onClick={() => removeItem(selected.id)}>🗑</button>
        </div>
      )}

      <div className="mb-stage">
        <div className="mb-canvas" ref={canvasRef}
             style={{ background: board.background?.color || '#f4f1ea' }}
             onPointerDown={() => { setSelectedId(null); setEditingId(null); }}>
          {[...items].sort((a, b) => (a.z || 0) - (b.z || 0)).map(it => {
            const sel = it.id === selectedId;
            const style = {
              left: `${it.x}%`, top: `${it.y}%`, width: `${it.w}%`, height: `${it.h}%`,
              transform: `rotate(${it.rotation || 0}deg)`, zIndex: (it.z || 0) + 1,
            };
            return (
              <div key={it.id} className={`mb-item ${sel ? 'sel' : ''}`} style={style}
                   onPointerDown={(e) => startInteraction(e, it.id, 'drag')}
                   onDoubleClick={() => { if (it.type === 'text') { setSelectedId(it.id); setEditingId(it.id); } }}>
                {it.type === 'image' && (
                  <>
                    {itemVisual(it, canvasSize)}
                    {it.sourceUrl && (
                      <a className="mb-source-link" href={it.sourceUrl} target="_blank" rel="noopener noreferrer"
                         title={`Source: ${it.sourceUrl}`}
                         onPointerDown={(e) => e.stopPropagation()}
                         onClick={(e) => e.stopPropagation()}>↗</a>
                    )}
                  </>
                )}
                {it.type === 'swatch' && itemVisual(it, canvasSize)}
                {it.type === 'text' && (
                  editingId === it.id ? (
                    <textarea className="mb-text-edit" autoFocus
                              value={it.text || ''}
                              onChange={(e) => updateItem(it.id, { text: e.target.value })}
                              style={{ fontSize: fontPx(it), color: it.color, textAlign: it.align, fontWeight: it.bold ? 700 : 400, fontStyle: it.italic ? 'italic' : 'normal' }}
                              onPointerDown={(e) => e.stopPropagation()}
                              onBlur={() => setEditingId(null)} />
                  ) : itemVisual(it, canvasSize)
                )}
                {sel && (
                  <>
                    <div className="mb-handle mb-rotate" onPointerDown={(e) => startInteraction(e, it.id, 'rotate')} title="Rotate" />
                    <div className="mb-handle mb-resize" onPointerDown={(e) => startInteraction(e, it.id, 'resize')} title="Resize" />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <p className="mb-hint">Paste an image (Ctrl/⌘+V) to drop a screenshot or copied web image · drag to move · corner handle to resize · top handle to rotate · double-click text to edit · ⊞ Tile a material swatch then grow the box to fill an area · Delete removes.</p>
      </div>

      {pickerOpen && (
        <div className="mb-picker-overlay" onClick={() => setPickerOpen(false)}>
          <div className="mb-picker" onClick={(e) => e.stopPropagation()}>
            <div className="mb-picker-head">
              <div className="mb-picker-tabs">
                <button className={pickerTab === 'products' ? 'on' : ''} onClick={() => setPickerTab('products')}>Project products</button>
                <button className={pickerTab === 'upload' ? 'on' : ''} onClick={() => setPickerTab('upload')}>Upload</button>
              </div>
              <button className="mb-picker-close" onClick={() => setPickerOpen(false)}>×</button>
            </div>
            <div className="mb-picker-body">
              {pickerTab === 'products' ? (
                pickerLoading ? <div className="mb-empty">Loading…</div>
                : pickerImages.length === 0 ? <div className="mb-empty">No product images on this project yet. Try the Upload tab.</div>
                : (
                  <div className="mb-picker-grid">
                    {pickerImages.map((im, i) => (
                      <button key={i} className="mb-picker-img" title={im.label}
                              onClick={() => handlePicked(im.url)}>
                        <img src={im.url} alt={im.label} />
                      </button>
                    ))}
                  </div>
                )
              ) : (
                <div className="mb-upload">
                  <p>Add a photo from your computer (PNG, JPG, WebP, GIF — up to 25MB).</p>
                  <button className="mb-btn mb-btn-primary" disabled={pickerLoading} onClick={() => uploadInput.current?.click()}>
                    {pickerLoading ? 'Uploading…' : 'Choose image'}
                  </button>
                  <input ref={uploadInput} type="file" accept="image/*" hidden onChange={onUploadFile} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {presenting && (
        <div className="mb-present" ref={presRef}>
          <button className="mb-present-exit" onClick={() => setPresenting(false)} title="Exit presentation (Esc)">×</button>
          <div className="mb-present-stage" ref={presStageRef}>
            <div className="mb-present-canvas"
                 style={{ width: presSize.w, height: presSize.h, background: board.background?.color || '#f4f1ea' }}>
              {[...items].sort((a, b) => (a.z || 0) - (b.z || 0)).map(it => (
                <div key={it.id} className="mb-present-item"
                     style={{ left: `${it.x}%`, top: `${it.y}%`, width: `${it.w}%`, height: `${it.h}%`,
                              transform: `rotate(${it.rotation || 0}deg)`, zIndex: (it.z || 0) + 1 }}>
                  {itemVisual(it, presSize)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {brushItem && (
        <div className="mb-brush-overlay" onPointerDown={(e) => { if (e.target === e.currentTarget) closeBrush(); }}>
          <div className="mb-brush">
            <div className="mb-brush-head">
              <strong>Touch up cutout</strong>
              <button className="mb-picker-close" onClick={closeBrush}>×</button>
            </div>
            <div className="mb-brush-tools">
              <button className="mb-mini mb-mini-wide" aria-pressed={brushMode === 'erase'} onClick={() => setBrushMode('erase')} title="Paint to remove">Erase</button>
              <button className="mb-mini mb-mini-wide" aria-pressed={brushMode === 'restore'} onClick={() => setBrushMode('restore')} title="Paint the original back">Restore</button>
              <label className="mb-brush-size">Brush
                <input type="range" min="6" max="140" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
              </label>
            </div>
            <div className="mb-brush-stage">
              <canvas ref={brushCanvasRef} className="mb-brush-canvas"
                      onPointerDown={brushStart} onPointerMove={brushMove} onPointerUp={brushEnd} onPointerLeave={brushEnd} />
            </div>
            <div className="mb-brush-foot">
              <span className="mb-hint" style={{ margin: 0 }}>Paint over areas to remove; switch to Restore to paint the original back. Checkerboard = transparent.</span>
              <div>
                <button className="mb-btn" onClick={closeBrush}>Cancel</button>
                <button className="mb-btn mb-btn-primary" disabled={brushBusy} onClick={applyBrush}>{brushBusy ? 'Saving…' : 'Apply'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {msg && <div className="mb-msg mb-msg-float" onClick={() => setMsg(null)}>{msg}</div>}
    </div>
  );
};

export default MoodBoardEditor;
